<template>
  <div>
    <!-- Main Navbar -->
    <nav
      class="fixed top-0 left-0 w-full z-[100] h-[70px] px-4 md:px-8 flex items-center justify-between bg-[#120F14]"
    >
      <!-- Left Section -->
      <div class="flex items-center gap-4 w-full">
        <!-- Mobile Menu Icon -->

        <button @click="toggleMobileMenu" class="lg:hidden text-white focus:outline-none">
          <svg
            v-if="!mobileMenuOpen"
            xmlns="http://www.w3.org/2000/svg"
            class="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-7 w-7"
          >
            <path
              fill-rule="evenodd"
              d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>

        <a href="/" rel="noopener noreferrer" class="hidden lg:block">
          <img
            src="@/assets/revamp/logo.png"
            alt="Maherco Logo"
            class="w-[100px] h-auto"
          />
        </a>
      </div>

      <!-- Right Section -->
      <div class="flex justify-end items-center gap-3 w-full">
        <!-- Language Dropdown -->
        <div class="relative">
          <button
            @click="toggleLanguageDropdown"
            class="flex items-center gap-2 text-white font-montserrat text-sm hover:opacity-80 transition-opacity"
          >
            <div
              class="w-5 h-5 shrink-0"
              :style="`
                -webkit-mask-image: url(${require('@/assets/revamp/icons/language.svg')});
                mask-image: url(${require('@/assets/revamp/icons/language.svg')});
                background-color: #FFFFFF;
                -webkit-mask-size: contain;
                mask-size: contain;
                -webkit-mask-repeat: no-repeat;
                mask-repeat: no-repeat;
                -webkit-mask-position: center;
                mask-position: center;
              `"
            ></div>
            <span class="hidden sm:block">{{ $t('Sidebar.Language') }}</span>
          </button>

          <!-- Dropdown Menu -->
          <div
            v-if="languageDropdownOpen"
            class="absolute right-0 mt-2 w-32 bg-[#1A1D24] text-white rounded-lg shadow-lg z-50 border border-gray-700 overflow-hidden"
          >
            <button
              @click="setLanguage('English', 'en')"
              class="block px-4 py-2.5 hover:bg-white/10 w-full text-left text-sm"
            >
              {{ $t('Common.English') }}
            </button>
            <button
              @click="setLanguage('Arabic', 'ar')"
              class="block px-4 py-2.5 hover:bg-white/10 w-full text-left text-sm"
            >
              {{ $t('Common.Arabic') }}
            </button>
          </div>
        </div>

        <!-- Sign In Button -->
        <button
          v-if="!isUserLoggedIn"
          @click="handleLogin()"
          class="px-4 md:px-6 py-2 bg-[#2EBBF0] hover:bg-[#26a8d9] text-black font-bold text-sm tracking-wide rounded-[10px] transition-colors duration-200 whitespace-nowrap shrink-0"
        >
          {{ $t("Button.Sign_In") }}
        </button>
      </div>
    </nav>

    <!-- Mobile Sidebar Drawer with Dropdown -->
    <transition name="slide">
      <div
        v-if="mobileMenuOpen"
        class="fixed top-0 left-0 h-full w-full bg-[#120F14]/95 backdrop-blur-lg transform transition-transform duration-300 ease-in-out z-[90] overflow-y-auto"
      >
        <div class="flex flex-col min-h-[calc(100vh-100px)] mt-[85px] pb-10 px-8 text-white font-bold font-oxanium text-lg uppercase">
          <!-- User Profile Section (Top of Drawer) -->
          <div 
            v-if="isUserLoggedIn"
            class="flex items-center gap-3 bg-[#1A1D24] border border-white/5 rounded-[12px] p-3 mb-6 flex-shrink-0"
          >
            <img
              :src="$store.state.avatar || 'https://i.pravatar.cc/80?img=12'"
              alt="User Avatar"
              class="h-10 w-10 rounded-full object-cover border-2 border-[#BC13FE] flex-shrink-0"
            />
            <div class="flex flex-col min-w-0">
              <p
                class="text-white font-bold text-sm tracking-wide overflow-hidden text-ellipsis whitespace-nowrap"
                style="font-family: 'Montserrat', sans-serif"
              >
                {{ $store.state.username || 'Username' }}
              </p>
              <span class="text-xs text-[#BEFF2E] font-medium mt-0.5">{{ $t('Sidebar.Member') }}</span>
            </div>
          </div>

          <!-- Navigation list -->
          <nav class="flex flex-col space-y-1 mb-6">
            <template v-for="item in filteredNavItems" :key="item.id">
              <router-link
                v-if="!item.dropdown"
                :to="item.link"
                @click="toggleMobileMenu"
                class="group flex items-center gap-3.5 w-full py-3 text-left transition duration-200"
                :class="isActive(item) ? 'text-[#BEFF2E] font-extrabold' : 'text-[#B9CACB] hover:text-white hover:pl-2'"
              >
                <!-- Dynamic Tinted Icon -->
                <div
                  v-if="getIcon(item.id)"
                  class="w-5 h-5 transition-all duration-200"
                  :class="isActive(item) ? 'bg-[#BEFF2E]' : 'bg-[#B9CACB] group-hover:bg-white'"
                  :style="`
                    -webkit-mask-image: url(${getIcon(item.id)});
                    mask-image: url(${getIcon(item.id)});
                    -webkit-mask-size: contain;
                    mask-size: contain;
                    -webkit-mask-repeat: no-repeat;
                    mask-repeat: no-repeat;
                    -webkit-mask-position: center;
                    mask-position: center;
                  `"
                ></div>
                <span>{{ item.name }}</span>
              </router-link>

              <!-- Dropdown Items -->
              <div v-else class="w-full">
                <button
                  @click.prevent="toggleDropdown(item.id)"
                  class="group flex items-center justify-between w-full py-3 text-left uppercase transition duration-200"
                  :class="isActive(item) ? 'text-[#BEFF2E] font-extrabold' : 'text-[#B9CACB] hover:text-white'"
                >
                  <div class="flex items-center gap-3.5">
                    <!-- Dynamic Tinted Icon -->
                    <div
                      v-if="getIcon(item.id)"
                      class="w-5 h-5 transition-all duration-200"
                      :class="isActive(item) ? 'bg-[#BEFF2E]' : 'bg-[#B9CACB] group-hover:bg-white'"
                      :style="`
                        -webkit-mask-image: url(${getIcon(item.id)});
                        mask-image: url(${getIcon(item.id)});
                        -webkit-mask-size: contain;
                        mask-size: contain;
                        -webkit-mask-repeat: no-repeat;
                        mask-repeat: no-repeat;
                        -webkit-mask-position: center;
                        mask-position: center;
                      `"
                    ></div>
                    <span>{{ item.name }}</span>
                  </div>
                  <svg
                    class="w-4 h-4 transition-transform duration-300"
                    :class="{ 'rotate-180': dropdowns[item.id] }"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <transition
                  enter-active-class="transition-all duration-300 ease-in-out"
                  enter-from-class="opacity-0 max-h-0 scale-y-90"
                  enter-to-class="opacity-100 max-h-40 scale-y-100"
                  leave-active-class="transition-all duration-300 ease-in-out"
                  leave-from-class="opacity-100 max-h-40 scale-y-100"
                  leave-to-class="opacity-0 max-h-0 scale-y-90"
                >
                  <div
                    v-show="dropdowns[item.id]"
                    class="pl-9 mt-1 space-y-1 text-sm text-[#B9CACB] overflow-hidden"
                  >
                    <router-link
                      v-for="(sub, index) in item.dropdown"
                      :key="index"
                      :to="{
                        path: item.id === 'store' ? '/store' : '/leaderboards',
                        query: item.id === 'store' ? { type: 'kick' } : {},
                      }"
                      @click="toggleMobileMenu"
                      class="block py-2 hover:text-[#BEFF2E] transition duration-200 hover:pl-1"
                      active-class="text-[#BEFF2E] font-extrabold"
                    >
                      - {{ sub }}
                    </router-link>
                  </div>
                </transition>
              </div>
            </template>
          </nav>

          <!-- Bottom Section (Join Community & Sign In / Logout) -->
          <div class="mt-auto pt-6 border-t border-gray-800/40 w-full flex flex-col gap-4">
            <!-- Join Community Button -->
            <a
              href="https://discord.com/invite/N764VWuGyn"
              target="_blank"
              class="flex items-center justify-center bg-[#6B0099] hover:bg-[#7b00b3] text-white font-bold transition-all duration-300 rounded-[12px] w-full gap-2.5 py-3.5 px-4"
            >
              <img
                src="@/assets/revamp/icons/community.png"
                class="h-5 w-auto object-contain brightness-0 invert"
                alt="Community"
              />
              <span class="text-sm font-semibold tracking-wide whitespace-nowrap">{{ $t('Sidebar.JoinCommunity') }}</span>
            </a>

            <!-- Logout Button (only when logged in) -->
            <button
              v-if="isUserLoggedIn"
              @click="handleLogout"
              class="block w-full text-left py-3 text-red-500 hover:text-red-400 uppercase transition duration-200 font-bold"
            >
              {{ $t('Sidebar.Logout') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { encryptStorage } from "@/store";

export default {
  data() {
    const languages = [
      { name: "English", code: "en" },
      { name: "Arabic", code: "ar" },
    ];
    const storedLang = localStorage.getItem("selectedLanguage") || "en";
    const selectedLangObj =
      languages.find((lang) => lang.code === storedLang) || languages[0];

    return {
      mobileMenuOpen: false,
      profileDropdown: false,
      languageDropdownOpen: false,
      selectedLanguage: selectedLangObj.name,
      navItems: [
        { id: "home", name: this.$t("Sidebar.Home"), link: "/" },
        { id: "promotions", name: this.$t("Sidebar.Promotions"), link: "/promotions" },
        { id: "rewards", name: this.$t("Sidebar.Rewards"), link: "/rewards" },
        {
          id: "leaderboards",
          name: this.$t("Sidebar.Leaderboards"),
          link: "/leaderboards",
        },
        {
          id: "store",
          name: this.$t("Sidebar.Store"),
          link: null,
          dropdown: [this.$t("Sidebar.KickStore")]
        },
        { id: "raffle", name: this.$t("Sidebar.Raffle"), link: "/raffle" },
        {
          id: "guessTheBalance",
          name: this.$t("Sidebar.GuessTheBalance"),
          link: "/guess-the-balance",
        },
        { id: "signup-guide", name: this.$t("Sidebar.Sign_Up_Guide"), link: "/signup-guide" },
        { id: "wager-rewards", name: this.$t("Sidebar.WagerRewards"), link: "/wager-rewards" },
      ],
      dropdowns: {
        leaderboards: false,
      },
    };
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
    filteredNavItems() {
      return this.navItems;
    },
  },
  methods: {
    getIcon(id) {
      try {
        if (id === "home") return require("@/assets/revamp/icons/home.svg");
        if (id === "promotions") return require("@/assets/revamp/icons/promotions.svg");
        if (id === "rewards") return require("@/assets/revamp/icons/rewards.svg");
        if (id === "leaderboards") return require("@/assets/revamp/icons/leaderboards.svg");
        if (id === "store") return require("@/assets/revamp/icons/store.svg");
        if (id === "raffle") return require("@/assets/revamp/icons/raffle.svg");
        if (id === "guessTheBalance") return require("@/assets/revamp/icons/gtb.svg");
        if (id === "signup-guide") return require("@/assets/revamp/icons/sign-up.svg");
        if (id === "wager-rewards") return require("@/assets/revamp/icons/wager-rewards.svg");
        return null;
      } catch (e) {
        return null;
      }
    },
    isActive(item) {
      if (item.link) {
        return this.$route.path === item.link;
      }
      if (item.id === "leaderboards") {
        return this.$route.path.startsWith("/leaderboards");
      }
      return false;
    },
    toggleDropdown(id) {
      this.dropdowns = {
        ...this.dropdowns,
        [id]: !this.dropdowns[id],
      };
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    toggleLanguageDropdown() {
      this.languageDropdownOpen = !this.languageDropdownOpen;
    },
    setLanguage(name, code) {
      this.selectedLanguage = name;
      localStorage.setItem("selectedLanguage", code);
      this.$i18n.locale = code;
      this.languageDropdownOpen = false;
      window.location.reload();
    },
    handleLogin() {
      const loginUrl = `${process.env.VUE_APP_BASE_API}/api/auth/login/kick`;
      window.location.href = loginUrl;
    },
    handleLogout() {
      const token = localStorage.getItem("maherco-access-token");

      if (!token) {
        console.error("No token found, skipping API logout request.");
        return;
      }

      axios
        .post(
          process.env.VUE_APP_BASE_API + "/api/auth/logout",
          {},
          {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then(() => {
          console.log("Successfully logged out from API.");

          this.$store.commit("UPDATE_USER", null);
          this.$store.commit("UPDATE_USERNAME", null);
          this.$store.commit("UPDATE_AVATAR", { avatar: null });
          this.$store.commit("UPDATE_ISVERIFIED", false);

          encryptStorage.removeItem("vuex");

          localStorage.removeItem("vuex");
          localStorage.removeItem("maherco-access-token");

          this.$router.replace("/");

          this.mobileMenuOpen = false;
        })
        .catch((error) => {
          console.error("Error logging out:", error);
        });
    },
  },
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-enter-to {
  transform: translateX(0%);
  opacity: 1;
}
.slide-leave-from {
  transform: translateX(0%);
  opacity: 1;
}
.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
</style>
